import { CircularProgress } from '@material-ui/core';
import { CustomTheme } from '@nemuru-eng/js-api-client';
import React, { useEffect, useState } from 'react';

import createCustomTheme from '@/components/shared/components/ThemeManager/createCustomTheme';
import { useCurrentThemeDefinition } from '@/components/shared/components/ThemeManager/hooks/useCurrentThemeDefinition';
import Themer from '@/components/shared/components/ThemeManager/Themer';
import neutralTheme from '@/config/themes/themes/neutralTheme';
import { Theme } from '@/config/types';
import { useConfigContext } from '@/contexts/ConfigContext/ConfigContext';
import { useUserContext } from '@/contexts/UserContext/UserContext';
import { useZendeskChat } from '@/hooks/useZendeskChat';

export const DEFAULT_THEME_NAME = CustomTheme.NEMURU;
export const DEFAULT_THEME = neutralTheme;
export const INITIAL_THEME_SWITCH_DELAY = 0;
export const FADE_IN_ANIMATION_DURATION = 700;

export function ThemeManager(props: { children: React.ReactNode }) {
  const { appStarted } = useUserContext();
  const {
    state: { currentLayout, appTheme, darkMode },
    actions,
  } = useConfigContext();
  const { setZendeskChat } = useZendeskChat();
  const { theme } = useCurrentThemeDefinition();

  const [firstLoadCompleted, setFirstLoadCompleted] = useState<boolean>(false);

  const appReady = appStarted && currentLayout;

  useEffect(() => {
    if (theme) {
      actions.changeTheme({ theme, darkMode });
    }
    if (theme && appReady && !firstLoadCompleted) {
      setTimeout(() => {
        setFirstLoadCompleted(true);
      }, INITIAL_THEME_SWITCH_DELAY);
    }
  }, [theme, appReady]);

  useEffect(() => {
    if (appTheme) {
      document.body.style.backgroundColor = appTheme.background ?? 'white';
      // Set main theming customizations in CSS variables, so that dependencies (e.g. providers) can use them
      setHslColorsFromTheme(appTheme);
    }
    if (appTheme?.zendeskCustomerSupport) {
      setZendeskChat();
    }
  }, [appTheme]);

  return (
    <>
      {!firstLoadCompleted && <CenteredCircularProgress />}
      <div
        style={{
          transition: `opacity ${FADE_IN_ANIMATION_DURATION}ms ease`,
          ...(firstLoadCompleted ? {} : { opacity: 0, pointerEvents: 'none' }),
        }}
      >
        <Themer theme={appTheme ?? DEFAULT_THEME}>{props.children}</Themer>
      </div>
    </>
  );
}

const CenteredCircularProgress = () => {
  return (
    <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)' }}>
      <CircularProgress color="inherit" thickness={4} size={40} />{' '}
    </div>
  );
};

const setHslColorsFromTheme = (theme: Theme) => {
  try {
    document.documentElement.style.setProperty('--background-color', theme.background ?? '#fff');
    document.documentElement.style.setProperty('--background', hexToHsl(theme.background ?? '#fff'));

    // Incorporate defaults to theme customized options
    const fullTheme = createCustomTheme(theme);

    document.documentElement.style.setProperty('--foreground', hexToHsl(fullTheme.palette.text.primary));
    document.documentElement.style.setProperty('--primary', hexToHsl(fullTheme.palette.primary.main));
    document.documentElement.style.setProperty(
      '--primary-foreground',
      hexToHsl(fullTheme.palette.primary.contrastText),
    );
    document.documentElement.style.setProperty('--secondary', hexToHsl(fullTheme.palette.secondary.main));
    document.documentElement.style.setProperty(
      '--secondary-foreground',
      hexToHsl(fullTheme.palette.secondary.contrastText),
    );
    document.documentElement.style.setProperty('--muted', hexToHsl(fullTheme.palette.grey[100]));
    document.documentElement.style.setProperty('--muted-foreground', hexToHsl(fullTheme.palette.text.secondary));
    document.documentElement.style.setProperty('--destructive', hexToHsl(fullTheme.palette.error.main));
    document.documentElement.style.setProperty(
      '--destructive-foreground',
      hexToHsl(fullTheme.palette.error.contrastText),
    );
    document.documentElement.style.setProperty('--border', hexToHsl(fullTheme.palette.grey[200]));
  } catch {}
};

const hexToHsl = (hex: string) => {
  // Convert hex to RGB
  let r = 0,
    g = 0,
    b = 0;
  if (hex.length === 4) {
    r = parseInt(hex[1] + hex[1], 16);
    g = parseInt(hex[2] + hex[2], 16);
    b = parseInt(hex[3] + hex[3], 16);
  } else if (hex.length === 7) {
    r = parseInt(hex.slice(1, 3), 16);
    g = parseInt(hex.slice(3, 5), 16);
    b = parseInt(hex.slice(5, 7), 16);
  } else {
    throw new Error('Invalid hex format');
  }

  // Normalize RGB values
  r /= 255;
  g /= 255;
  b /= 255;

  // Find greatest and smallest channel values
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);

  // Calculate luminance
  const l = (max + min) / 2;

  let h, s;

  if (max === min) {
    // Achromatic (gray)
    h = 0;
    s = 0;
  } else {
    const d = max - min;
    // Calculate saturation
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    // Calculate hue
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  if (h === undefined) {
    throw new Error('Invalid HSL format');
  }

  return `${Math.round(h * 360)}, ${Math.round(s * 100)}%, ${Math.round(l * 100)}%`;
};
